import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Autocomplete,
} from '@mui/material';
import { Article, TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { momentumService } from '../../services/momentumService';
import { usePagesList } from '../../hooks/usePagesList';

// API response types
interface MetricDelta {
  slope: number;
  trend: 'up' | 'down' | 'improving' | 'declining';
  p_value: number;
  r_squared: number;
}

interface MomentumApiResponse {
  id: number;
  tenantId: number;
  pageId: number;
  pageUrl: string;
  engine: string;
  computedAt: string;
  windowDays: number;
  insights?: string[];
  overallState: 'POSITIVE' | 'NEUTRAL' | 'DECLINING';
  overallMomentum: number;
  metricsJson: {
    deltas: Record<string, MetricDelta>;
    start_date: string;
    end_date: string;
    rationale: {
      reasons: string[];
      positive_signals: number;
      negative_signals: number;
    };
  };
  hasControlGroup: boolean;
  dataPoints: number;
  dataCompleteness: number;
}

export default function MomentumAnalyticsPage() {
  const { pages, loading: loadingPages } = usePagesList();
  const [pageId, setPageId] = useState<number | ''>('');
  const [engine, setEngine] = useState<'google' | 'bing'>('google');
  const [windowDays, setWindowDays] = useState<28 | 90>(28);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MomentumApiResponse | null>(null);

  const load = async () => {
    if (pageId === '' || pageId === undefined) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await momentumService.getMomentum(Number(pageId), engine, windowDays);
      setData(res as unknown as MomentumApiResponse);
    } catch (e: any) {
      setError(e?.message || 'Failed to load momentum');
    } finally {
      setLoading(false);
    }
  };

  const getStateColor = (state: string) => {
    if (state === 'POSITIVE') return 'success';
    if (state === 'DECLINING') return 'error';
    return 'default';
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'up' || trend === 'improving') return <TrendingUp color="success" fontSize="small" />;
    if (trend === 'down' || trend === 'declining') return <TrendingDown color="error" fontSize="small" />;
    return <TrendingFlat color="disabled" fontSize="small" />;
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">Momentum</Typography>

      {/* Controls */}
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Autocomplete
              size="small"
              sx={{ width: 280 }}
              options={pages}
              value={pages.find(p => p.id === pageId) || null}
              onChange={(_, newValue) => setPageId(newValue?.id || '')}
              getOptionLabel={(option) => option.label}
              loading={loadingPages}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Page"
                  placeholder="Select Page"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Article sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Article fontSize="small" color="action" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {option.url.length > 40 ? '...' + option.url.slice(-40) : option.url}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <TextField
              label="Engine"
              select
              size="small"
              value={engine}
              onChange={(e) => setEngine(e.target.value as 'google' | 'bing')}
              sx={{ width: 160 }}
            >
              <MenuItem value="google">Google</MenuItem>
              <MenuItem value="bing">Bing</MenuItem>
            </TextField>
            <TextField
              label="Window"
              select
              size="small"
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value) as 28 | 90)}
              sx={{ width: 160 }}
            >
              <MenuItem value={28}>28 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
            </TextField>
            <Button variant="contained" onClick={load} disabled={loading || pageId === ''}>
              Load
            </Button>
            {loading && <CircularProgress size={20} />}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </CardContent>
      </Card>

      {/* Results */}
      {data && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Chip 
                label={data.overallState} 
                color={getStateColor(data.overallState) as any} 
              />
              <Chip label={`Momentum: ${data.overallMomentum?.toFixed(1) || 0}`} />
              <Chip label={`${data.dataCompleteness?.toFixed(0) || 0}% complete`} variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Computed: {new Date(data.computedAt).toLocaleString()} • Engine: {data.engine?.toUpperCase()} • Window: {data.windowDays}d
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Period: {data.metricsJson?.start_date} to {data.metricsJson?.end_date} • Data points: {data.dataPoints}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Metrics Deltas */}
            <Typography variant="subtitle1" gutterBottom>Trend Analysis</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
              {data.metricsJson?.deltas && Object.entries(data.metricsJson.deltas).map(([metric, delta]) => (
                <Box key={metric} sx={{ minWidth: 200, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">{metric.toUpperCase()}</Typography>
                    {getTrendIcon(delta.trend)}
                  </Stack>
                  <Typography variant="body2">Slope: {delta.slope?.toFixed(4)}</Typography>
                  <Typography variant="body2">Trend: {delta.trend}</Typography>
                  <Typography variant="body2" color={delta.p_value < 0.05 ? 'success.main' : 'text.secondary'}>
                    p-value: {delta.p_value?.toFixed(4)} {delta.p_value < 0.05 && '✓'}
                  </Typography>
                  <Typography variant="body2">R²: {delta.r_squared?.toFixed(4)}</Typography>
                </Box>
              ))}
            </Stack>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Rationale */}
            <Typography variant="subtitle1" gutterBottom>Rationale</Typography>
            <Typography variant="body2">
              Positive signals: {data.metricsJson?.rationale?.positive_signals || 0} | 
              Negative signals: {data.metricsJson?.rationale?.negative_signals || 0}
            </Typography>
            {data.metricsJson?.rationale?.reasons?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {data.metricsJson.rationale.reasons.map((reason, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">• {reason}</Typography>
                ))}
              </Box>
            )}
            
            {/* Insights */}
            {data.insights && data.insights.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>Insights</Typography>
                {data.insights.map((insight, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">• {insight}</Typography>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
